import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Stack,
  Breadcrumbs,
  Typography,
  ButtonBase,
  Button,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { DataTable, FilterSearch, MainLayout } from '../../../../ui';
import { Permission } from '../../../../utils/helpers/permissions';
import PermissionGate from '../../../../utils/hooks/PermissionGate';
import AssessmentFilter from './assessment_list_table/AssessmentFilter';
import { getColumns } from './Functions';
import { useAssessments } from './Hooks/UseAssesments';

export const AssessmentList: React.FC = () => {
  const {
    activeTab,
    setActiveTab,
    searchObject,
    setSearchObject,
    loading,
    paginatedAssessments,
    totalPages,
    tabs,
    onRowClick,
    pageNumber,
    onPageChange,
    hasFetchedOnce,
    totalRecords,
    currentSort,
    handleSortChange,
    handleSearchTermChange,
  } = useAssessments();

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Breadcrumb Header */}
      <Box
        component="nav"
        aria-label="Breadcrumb"
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1.5,
          px: { xs: 2, md: 3 },
        }}
      >
        <Breadcrumbs aria-label="breadcrumb">
          <Typography
            component={Link}
            to="#"
            sx={{
              fontWeight: 600,
              fontSize: 14,
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            HierEvaluation
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Tabs Row */}
      <Box
        sx={{
          display: 'flex',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, md: 3 },
        }}
      >
        <Stack direction="row" sx={{ width: '100%' }}>
          {tabs.map((tab: { label: string }, index: number) => {
            const isActive = activeTab === index;
            return (
              <ButtonBase
                key={tab.label}
                onClick={() => setActiveTab(index)}
                sx={{
                  px: 2,
                  py: 1.25,
                  fontWeight: 600,
                  fontSize: 14,
                  color: isActive ? 'primary.main' : 'text.secondary',
                  borderBottom: '3px solid',
                  borderColor: isActive ? 'primary.main' : 'transparent',
                  transition: 'all 200ms ease',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                {tab.label}
              </ButtonBase>
            );
          })}
        </Stack>
      </Box>

      {/* Main Content */}
      <MainLayout>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'flex-end' }}
          spacing={2.5}
          sx={{ mt: 2.5, mb: 2 }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'flex-end' }}
            spacing={2}
            sx={{ width: '100%' }}
          >
            <AssessmentFilter
              searchObject={searchObject}
              setSearchObject={setSearchObject}
            />
            <FilterSearch
              searchText="Search by Assessment Name"
              onSearch={(searchTerm: string) => {
                setSearchObject({
                  ...searchObject,
                  timeRange: searchObject.timeRange || {
                    startDate: new Date(
                      new Date().setFullYear(new Date().getFullYear() - 1),
                    )
                      .toISOString()
                      .split('T')[0],
                    endDate: new Date().toISOString().split('T')[0],
                  },
                  assessmentName: searchTerm,
                  page: 1,
                });
              }}
            />
          </Stack>

          <PermissionGate
            requiredPermissions={[
              Permission.ASSESSMENTS_CREATE,
              Permission.QUESTIONS_VIEW,
            ]}
            matchAnyPermission={false}
          >
            <Button
              component={Link}
              to="/assessment"
              variant="contained"
              color="primary"
              startIcon={<Plus size={16} />}
              sx={{
                width: { xs: '100%', md: 240 },
                height: 40,
                fontSize: 13,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Create New Assessment
            </Button>
          </PermissionGate>
        </Stack>

        <Box sx={{ width: '100%', height: { xs: 'calc(100dvh - 260px)', sm: 'calc(100vh - 230px)' }, minHeight: 360, minWidth: 0 }}>
          <DataTable
            isLoading={!hasFetchedOnce || loading}
            columns={getColumns()}
            data={paginatedAssessments ?? []}
            rowsPerPage={10}
            onPageChange={onPageChange}
            currentPage={pageNumber}
            totalPages={totalPages}
            onClickRow={onRowClick}
            totalRecords={totalRecords}
            onSortChange={handleSortChange}
            sortConfig={
              currentSort
                ? [
                    {
                      key: currentSort.key,
                      direction: currentSort.direction,
                    },
                  ]
                : []
            }
          />
        </Box>
      </MainLayout>
    </Box>
  );
};
